/**
 * The funnel-icon column filter — Excel's AutoFilter (P30).
 *
 * Ported from `reporting_app/web/src/components/ui/ColumnFilter.tsx`, with two
 * DRA additions and one DRA subtraction:
 *
 *   + counts beside every value ("Needs review 30"), so a reviewer knows what a
 *     click is worth before making it;
 *   + a `date` mode, because `Created` filters on the SERVER as a range and a
 *     checkbox list of 54 distinct timestamps would be useless;
 *   − nothing is remembered in localStorage. reporting_app deliberately keeps
 *     its filters in memory only; DRA keeps them in the URL (P22). Both rules
 *     forbid the same thing — a filter that silently survives into a visit
 *     nobody asked for it in.
 *
 * Lives inside a `SortableHeader`'s `filter` slot. Every click inside the
 * popover stops propagation so it never toggles the header's sort or interferes
 * with a column resize — the same guard `ColumnResizeHandle` uses.
 *
 * `values` is given by the caller, never derived here, and the difference
 * matters: a CLIENT column offers the values its loaded rows actually have, a
 * SERVER column offers its full canonical list. Deriving a server column's list
 * from the loaded rows would make a filter impossible to widen again — the rows
 * carrying the unchecked values are exactly the ones the server left out.
 *
 * =========================================================================
 * `emptyMeans` — THE ONE PLACE THE TWO KINDS OF COLUMN REALLY DIFFER
 * =========================================================================
 * Excel's AutoFilter starts with every box ticked, because an unfiltered column
 * genuinely holds every value. That is true of a CLIENT column here, so those
 * use `emptyMeans="all"` and behave exactly like Excel: untick to exclude.
 *
 * It is NOT true of a SERVER column. An absent `status` parameter does not mean
 * "every status" — `list.ts` falls back to the open-work default, which
 * excludes Closed and Cancelled. Ticking all seven boxes to represent that
 * would state on screen that closed tickets are included when they are not. So
 * server columns use `emptyMeans="none"`: nothing ticked means "whatever the
 * endpoint does by default", and ticking a value adds it to the parameter —
 * which is also what the P22 chips this menu replaced did.
 */
import { useEffect, useRef, useState } from 'react';
import { Filter } from 'lucide-react';
import { IconButton } from './Button';
import type { ColumnFilterState } from '../../hooks/useColumnFilters';

export interface FilterValue {
    value: string;
    /** What a person reads. The raw value stays reachable as the `title`. */
    label: string;
    count?: number;
    title?: string;
}

interface Props {
    label: string;
    values: FilterValue[];
    state: ColumnFilterState | undefined;
    onChange: (next: ColumnFilterState | null) => void;
    /** `list` (default) checkboxes; `numeric` adds a min/max; `date` is a from/to range only. */
    mode?: 'list' | 'numeric' | 'date';
    /** Date mode is a range in ISO days, held by the caller (a server parameter). */
    dateFrom?: string | null;
    dateTo?: string | null;
    onDateChange?: (from: string | null, to: string | null) => void;
    /** What an ABSENT filter means for this column. See the header. */
    emptyMeans?: 'all' | 'none';
    active: boolean;
}

export function ColumnFilter({
    label, values, state, onChange, mode = 'list', dateFrom, dateTo, onDateChange,
    emptyMeans = 'all', active,
}: Props) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDocMouseDown = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDocMouseDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocMouseDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const selected = state?.selected ?? null;
    // With `emptyMeans="all"`, null means "everything checked" — materialised against the current
    // value list only when a box actually needs toggling, so an untouched filter never drifts into
    // a stored set that then falls behind a newly-loaded value.
    const effectiveSelected = selected ?? (emptyMeans === 'all' ? new Set(values.map(v => v.value)) : new Set<string>());
    const shown = search
        ? values.filter(v => v.label.toLowerCase().includes(search.toLowerCase()))
        : values;
    /**
     * With a value search typed, "All" means the values the search LEFT (P32).
     * Katia's case is why: BASWA's tickets carry six different ACTICIDE
     * articles, and ticking them one by one is six clicks for a question that
     * is one thought. Without a search typed the button is unchanged.
     */
    const allMeansShown = shown.length > 0 && shown.length < values.length;

    const commit = (sel: Set<string> | null, min?: number | null, max?: number | null) => {
        const noMin = min === null || min === undefined;
        const noMax = max === null || max === undefined;
        if (sel === null && noMin && noMax) onChange(null);
        else onChange({ selected: sel, min: noMin ? null : min, max: noMax ? null : max });
    };

    /**
     * WHAT AN INCLUSION LIST DOES TO A TICKET THAT ARRIVES LATER (P38, task 9)
     *
     * Measured on 2026-09-30: unticking ONE value in a client column materialises
     * every REMAINING value into the URL by name — `f.customer=A.+Helfer+SA|Aarolac
     * +AG|…`, eleven customers. It is Excel's own behaviour and it is what
     * `emptyMeans="all"` means. But a URL is not a spreadsheet: the reader keeps it,
     * sends it to a colleague, and comes back to it after lunch — and a customer
     * whose FIRST request arrives in the meantime is not in that list of eleven, so
     * it is dropped from a board somebody reads as their workload.
     *
     * Storing the excluded values instead would fix it at the root, and it is not
     * this phase's to do: `f.<column>=` is P30's URL contract, asserted by
     * `p30:verify`, and `useColumnFilters` does not know a column's `emptyMeans`.
     * So the rule is STATED, here, where the reader is making the decision — which
     * is the alternative the plan names and the one that cannot break a link
     * somebody already has.
     *
     * Only for an inclusion list on an `emptyMeans="all"` column. A server column
     * ticks to ADD, so a later arrival matching a ticked value is included by
     * construction and there is nothing to warn about.
     */
    const hidesLaterArrivals = emptyMeans === 'all' && selected !== null;

    /** The set that means "no restriction on this column", so the filter clears rather than pins. */
    const isNeutral = (sel: Set<string>) => (emptyMeans === 'all' ? sel.size === values.length : sel.size === 0);

    const toggleValue = (v: string) => {
        const next = new Set(effectiveSelected);
        if (next.has(v)) next.delete(v); else next.add(v);
        commit(isNeutral(next) ? null : next, state?.min, state?.max);
    };

    return (
        <span
            className="relative inline-block normal-case font-normal"
            ref={ref}
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
        >
            {/* P36, S2-10. This was `p-0.5` around a 12 px icon and measured
                16×16 — the smallest of the 53 focusable elements on `/tickets`
                that were under the 32 px floor, and there is one per column.
                The ICON is unchanged; only the target around it grew. */}
            <IconButton
                label={`Filter ${label}`}
                active={active}
                onClick={() => setOpen(o => !o)}
                aria-expanded={open}
                className="hover:bg-hair-2"
            >
                <Filter className="h-3 w-3" fill={active ? 'currentColor' : 'none'} />
            </IconButton>
            {open && (
                <div
                    role="dialog"
                    aria-label={`Filter ${label}`}
                    className="absolute left-0 top-full z-30 mt-1 w-60 cursor-default rounded-card border border-hair bg-surface p-2 text-body shadow-overlay"
                >
                    {mode === 'date' && (
                        <div className="mb-1 space-y-1 text-xs">
                            <label className="flex items-center justify-between gap-1">
                                <span className="text-gray-500">from</span>
                                <input
                                    type="date"
                                    aria-label={`${label} from`}
                                    value={dateFrom ?? ''}
                                    onChange={e => onDateChange?.(e.target.value || null, dateTo ?? null)}
                                    className="rounded border-gray-300 px-1 py-0.5 text-xs"
                                />
                            </label>
                            <label className="flex items-center justify-between gap-1">
                                <span className="text-gray-500">to</span>
                                <input
                                    type="date"
                                    aria-label={`${label} to`}
                                    value={dateTo ?? ''}
                                    onChange={e => onDateChange?.(dateFrom ?? null, e.target.value || null)}
                                    className="rounded border-gray-300 px-1 py-0.5 text-xs"
                                />
                            </label>
                        </div>
                    )}
                    {mode === 'numeric' && (
                        <div className="mb-2 flex items-center gap-1 text-xs">
                            <input
                                type="number"
                                placeholder="min"
                                aria-label={`${label} minimum`}
                                value={state?.min ?? ''}
                                onChange={e => commit(selected, e.target.value === '' ? null : Number(e.target.value), state?.max)}
                                className="w-full rounded border px-1 py-0.5"
                            />
                            <span className="text-muted">–</span>
                            <input
                                type="number"
                                placeholder="max"
                                aria-label={`${label} maximum`}
                                value={state?.max ?? ''}
                                onChange={e => commit(selected, state?.min, e.target.value === '' ? null : Number(e.target.value))}
                                className="w-full rounded border px-1 py-0.5"
                            />
                        </div>
                    )}
                    {mode !== 'date' && (
                        <>
                            {values.length > 8 && (
                                <input
                                    type="text"
                                    placeholder="Search values"
                                    aria-label={`Search ${label} values`}
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    className="mb-1.5 w-full rounded border px-1.5 py-1 text-xs"
                                />
                            )}
                            <div className="mb-1 flex items-center justify-between px-0.5 text-xs text-info">
                                <button
                                    type="button"
                                    title={allMeansShown ? `Select the ${shown.length} values the search leaves` : undefined}
                                    onClick={() => {
                                        if (allMeansShown) {
                                            const next = new Set(shown.map(v => v.value));
                                            commit(isNeutral(next) ? null : next, state?.min, state?.max);
                                            return;
                                        }
                                        commit(emptyMeans === 'all' ? null : new Set(values.map(v => v.value)), state?.min, state?.max);
                                    }}
                                >
                                    {allMeansShown ? 'All shown' : 'All'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => commit(emptyMeans === 'none' ? null : new Set(), state?.min, state?.max)}
                                >
                                    {emptyMeans === 'none' ? 'Clear' : 'None'}
                                </button>
                            </div>
                            <div className="max-h-48 overflow-auto border-t pt-1">
                                {shown.length === 0 && <div className="px-1 py-2 text-xs text-muted">—</div>}
                                {shown.map(v => (
                                    <label
                                        key={v.value}
                                        title={v.title}
                                        className="flex cursor-pointer items-center gap-1.5 rounded px-1 py-0.5 text-xs hover:bg-gray-50"
                                    >
                                        <input
                                            type="checkbox"
                                            // The visible label carries the count beside the
                                            // name, so the accessible name is set explicitly:
                                            // "Waiting on Sales", never "Waiting on Sales 2".
                                            aria-label={v.label}
                                            checked={effectiveSelected.has(v.value)}
                                            onChange={() => toggleValue(v.value)}
                                        />
                                        <span className="flex-1 truncate">{v.label}</span>
                                        {v.count !== undefined && <span className="shrink-0 text-muted">{v.count}</span>}
                                    </label>
                                ))}
                            </div>
                            {/*
                              * P38, task 9. Stated where the decision is made, and only
                              * while an inclusion list is actually in force — see
                              * `hidesLaterArrivals`. It is a `Banner` rather than a grey
                              * footnote because it is the one thing in this popover a
                              * reader would not otherwise find out: the filter is a list
                              * of names, not a rule, and a name that did not exist when
                              * they ticked cannot be on it.
                              */}
                            {hidesLaterArrivals && (
                                <p className="mt-1 border-t border-hair pt-1 text-xs text-muted">
                                    This keeps a list of {effectiveSelected.size} value{effectiveSelected.size === 1 ? '' : 's'} by
                                    name. A ticket with a new one is hidden until you clear the filter.
                                </p>
                            )}
                        </>
                    )}
                </div>
            )}
        </span>
    );
}
